import { lazy, Suspense, useContext, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import {
  DndContext,
  type DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { toast } from "sonner";
import { ChevronDown } from "lucide-react";
import MerchantContext from "../../../contexts/MerchantContext";
import { trpc } from "../../../trpc";
import { KanbanColumn } from "../../leads/kanban/KanbanColumn";
import { KanbanGroup } from "../../leads/kanban/KanbanGroup";
import { KanbanScrollRail } from "../../leads/kanban/KanbanScrollRail";
import { KanbanStageNavigator } from "../../leads/kanban/KanbanStageNavigator";
import type { StageNavItem } from "../../leads/kanban/KanbanStageNavigator";
import { TicketCard } from "./TicketCard";
import { TicketKanbanFilters } from "./TicketKanbanFilters";
import TicketDrawer from "./TicketDrawer";

const MaintenanceContent = lazy(
  () => import("../maintenance/MaintenanceContent")
);
import type { KanbanTicket, TicketKanbanFilters as Filters } from "./types";
import type { TicketStage } from "./constants";
import {
  TICKET_STAGE_ORDER,
  TICKET_STAGE_META,
  PIPELINE_AGENT_TOPIC,
} from "./constants";

// Position shape returned by getTicketPositions (grouped by stage)
type TicketPosition = {
  id: string;
  stage: string;
  status: string;
  order: number;
  displayName: string;
  isSystem: boolean;
};

type StageGroup = {
  stage: TicketStage;
  statuses: string[];
  positions: TicketPosition[];
};

type ServiceRequestContentProps = {
  pipelineId?: string;
};

// --- Internal: per-status column with its own infinite query ---

const StatusColumn = ({
  merchantId,
  pipelineId,
  status,
  displayName,
  onSelectTicket,
  employeeNameById,
  startDate,
  endDate,
}: {
  merchantId: string;
  pipelineId: string;
  status: string;
  displayName: string;
  onSelectTicket: (id: string) => void;
  employeeNameById: Map<string, string>;
  startDate?: string;
  endDate?: string;
}) => {
  const query = trpc.serviceRequests.listByStatus.useInfiniteQuery(
    { merchantId, status, pipelineId, limit: 25, startDate, endDate },
    { getNextPageParam: (lastPage) => lastPage.cursor ?? undefined }
  );

  const tickets = useMemo(
    () => query.data?.pages.flatMap((p) => p.data) ?? [],
    [query.data]
  );

  return (
    <KanbanColumn
      title={displayName}
      count={tickets.length}
      isLoading={query.isLoading}
      hasMore={!!query.hasNextPage}
      loadMoreDisabled={query.isFetchingNextPage}
      onLoadMore={() => query.fetchNextPage()}
      droppableId={`status:${status}`}
      droppableDisabled={false}
    >
      {tickets.length === 0 && !query.isLoading ? (
        <span className="px-1 py-2 text-xs text-gray-400">Sin tickets</span>
      ) : (
        tickets.map((ticket) => (
          <TicketCard
            key={ticket.id}
            ticket={ticket}
            onClick={onSelectTicket}
            employeeNameById={employeeNameById}
            draggable
          />
        ))
      )}
    </KanbanColumn>
  );
};

// --- Internal: filtered mode columns (single query, client-side grouping) ---

const FilteredColumns = ({
  merchantId,
  pipelineId,
  filters,
  stageGroups,
  onSelectTicket,
  employeeNameById,
}: {
  merchantId: string;
  pipelineId: string;
  filters: Filters;
  stageGroups: StageGroup[];
  onSelectTicket: (id: string) => void;
  employeeNameById: Map<string, string>;
}) => {
  const query = trpc.serviceRequests.listFiltered.useQuery(
    {
      merchantId,
      pipelineId,
      customerPhone: filters.phone || undefined,
      category: filters.category || undefined,
      assignedEmployeeId: filters.assignedEmployeeId || undefined,
      priority: (filters.priority || undefined) as "low" | "medium" | "high" | "urgent" | undefined,
      startDate: filters.startDate || undefined,
      endDate: filters.endDate || undefined,
      limit: 200,
    },
    { enabled: !!merchantId }
  );

  const grouped = useMemo(() => {
    const map = new Map<string, KanbanTicket[]>();
    for (const ticket of query.data?.data ?? []) {
      const list = map.get(ticket.status) ?? [];
      list.push(ticket);
      map.set(ticket.status, list);
    }
    return map;
  }, [query.data]);

  return (
    <>
      {stageGroups.map(({ stage, positions }) => {
        const meta = TICKET_STAGE_META[stage];
        return (
          <KanbanGroup
            key={stage}
            stageId={stage}
            label={meta.label}
            hint={meta.hint}
            accentClass={meta.accent}
          >
            {positions.map((pos) => {
              const tickets = grouped.get(pos.status) ?? [];
              return (
                <KanbanColumn
                  key={pos.status}
                  title={pos.displayName}
                  count={tickets.length}
                  isLoading={query.isLoading}
                  droppableId={`status:${pos.status}`}
                  droppableDisabled={false}
                >
                  {tickets.length === 0 && !query.isLoading ? (
                    <span className="px-1 py-2 text-xs text-gray-400">
                      Sin coincidencias
                    </span>
                  ) : (
                    tickets.map((ticket) => (
                      <TicketCard
                        key={ticket.id}
                        ticket={ticket}
                        onClick={onSelectTicket}
                        employeeNameById={employeeNameById}
                        draggable
                      />
                    ))
                  )}
                </KanbanColumn>
              );
            })}
          </KanbanGroup>
        );
      })}
    </>
  );
};

// --- Main component ---

const ServiceRequestContent = ({ pipelineId = "general" }: ServiceRequestContentProps) => {
  const { merchantId } = useContext(MerchantContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedTicketId, setSelectedTicketId] = useState<string | undefined>(
    () => searchParams.get("id") ?? undefined
  );

  // Fetch dynamic ticket positions for this pipeline
  const { data: ticketPositionsByStage } = trpc.pipeline.getTicketPositions.useQuery(
    { merchantId, pipelineId },
    { staleTime: 5 * 60 * 1000, enabled: !!merchantId }
  );

  // Build stage groups from dynamic positions
  const stageGroups = useMemo<StageGroup[]>(() => {
    if (!ticketPositionsByStage) return [];
    return TICKET_STAGE_ORDER
      .filter((stage) => {
        const positions = (ticketPositionsByStage as Record<string, TicketPosition[]>)[stage];
        return positions && positions.length > 0;
      })
      .map((stage) => {
        const positions = ((ticketPositionsByStage as Record<string, TicketPosition[]>)[stage] ?? [])
          .sort((a, b) => a.order - b.order);
        return {
          stage,
          statuses: positions.map((p) => p.status),
          positions,
        };
      });
  }, [ticketPositionsByStage]);

  // Flat list of all positions for the drawer
  const allTicketPositions = useMemo<TicketPosition[]>(
    () => stageGroups.flatMap((g) => g.positions),
    [stageGroups]
  );

  // Stage navigator items
  const stageNavItems = useMemo<StageNavItem[]>(
    () => stageGroups.map(({ stage }) => ({ id: stage, label: TICKET_STAGE_META[stage].label })),
    [stageGroups]
  );
  const railRef = useRef<HTMLDivElement>(null);

  // Filter state
  const [filters, setFilters] = useState<Filters>(() => ({
    phone: "",
    startDate: dayjs().startOf("month").format("YYYY-MM-DD"),
    endDate: dayjs().endOf("month").format("YYYY-MM-DD"),
    category: "",
    assignedEmployeeId: "",
    priority: "",
  }));

  const isFilterMode =
    filters.phone.length > 0 ||
    filters.category.length > 0 ||
    filters.assignedEmployeeId.length > 0 ||
    filters.priority.length > 0;

  // Employee prefetch
  const { data: employees } = trpc.employees.list.useQuery(
    { merchantId },
    { staleTime: 5 * 60 * 1000, enabled: !!merchantId }
  );
  const employeeNameById = useMemo(() => {
    const map = new Map<string, string>();
    employees?.forEach((e: { id: string; name: string }) =>
      map.set(e.id, e.name)
    );
    return map;
  }, [employees]);

  // DnD setup (disabled in filter mode)
  const utils = trpc.useUtils();
  const moveMutation = trpc.serviceRequests.moveStatus.useMutation();
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    if (isFilterMode) return;

    const { active, over } = event;
    if (!over) return;

    const ticketId = String(active.id);
    const sourceStatus = active.data.current?.status as string | undefined;
    const overId = String(over.id);
    if (!overId.startsWith("status:")) return;
    const targetStatus = overId.slice("status:".length);

    if (!sourceStatus || sourceStatus === targetStatus) return;

    const sourceKey = { merchantId, status: sourceStatus, pipelineId, limit: 25, startDate: filters.startDate, endDate: filters.endDate };
    const targetKey = { merchantId, status: targetStatus, pipelineId, limit: 25, startDate: filters.startDate, endDate: filters.endDate };
    const prevSource =
      utils.serviceRequests.listByStatus.getInfiniteData(sourceKey);
    const prevTarget =
      utils.serviceRequests.listByStatus.getInfiniteData(targetKey);

    const movedTicket = prevSource?.pages
      .flatMap((p) => p.data)
      .find((t) => t.id === ticketId);
    if (!movedTicket) return;

    if (prevSource) {
      utils.serviceRequests.listByStatus.setInfiniteData(sourceKey, {
        ...prevSource,
        pages: prevSource.pages.map((page) => ({
          ...page,
          data: page.data.filter((t) => t.id !== ticketId),
        })),
      });
    }

    if (prevTarget) {
      const updatedTicket = { ...movedTicket, status: targetStatus };
      const [firstPage, ...restPages] = prevTarget.pages;
      const newPages = firstPage
        ? [
            { ...firstPage, data: [updatedTicket, ...firstPage.data] },
            ...restPages,
          ]
        : prevTarget.pages;
      utils.serviceRequests.listByStatus.setInfiniteData(targetKey, {
        ...prevTarget,
        pages: newPages,
      });
    } else {
      utils.serviceRequests.listByStatus.invalidate(targetKey);
    }

    try {
      await moveMutation.mutateAsync({
        merchantId,
        id: ticketId,
        status: targetStatus,
      });
    } catch (error) {
      if (prevSource) {
        utils.serviceRequests.listByStatus.setInfiniteData(
          sourceKey,
          prevSource
        );
      }
      if (prevTarget) {
        utils.serviceRequests.listByStatus.setInfiniteData(
          targetKey,
          prevTarget
        );
      }
      toast.error("No pudimos mover el ticket. Intenta de nuevo.");
      console.error("moveStatus failed", error);
    }
  };

  const filterQueryKey = useMemo(
    () => ({
      merchantId,
      pipelineId,
      customerPhone: filters.phone || undefined,
      category: filters.category || undefined,
      assignedEmployeeId: filters.assignedEmployeeId || undefined,
      priority: (filters.priority || undefined) as "low" | "medium" | "high" | "urgent" | undefined,
      startDate: filters.startDate || undefined,
      endDate: filters.endDate || undefined,
      limit: 200,
    }),
    [merchantId, pipelineId, filters]
  );

  const handleFilterDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over) return;

    const ticketId = String(active.id);
    const sourceStatus = active.data.current?.status as string | undefined;
    const overId = String(over.id);
    if (!overId.startsWith("status:")) return;
    const targetStatus = overId.slice("status:".length);

    if (!sourceStatus || sourceStatus === targetStatus) return;

    const prev = utils.serviceRequests.listFiltered.getData(filterQueryKey);

    if (prev) {
      utils.serviceRequests.listFiltered.setData(filterQueryKey, {
        ...prev,
        data: prev.data.map((t) =>
          t.id === ticketId ? { ...t, status: targetStatus } : t
        ),
      });
    }

    try {
      await moveMutation.mutateAsync({
        merchantId,
        id: ticketId,
        status: targetStatus,
      });
      // Invalidate per-column caches so they're fresh when filters are cleared
      utils.serviceRequests.listByStatus.invalidate();
    } catch (error) {
      if (prev) {
        utils.serviceRequests.listFiltered.setData(filterQueryKey, prev);
      }
      toast.error("No pudimos mover el ticket. Intenta de nuevo.");
      console.error("moveStatus (filter mode) failed", error);
    }
  };

  const handleTicketSelect = (id: string) => {
    setSelectedTicketId(id);
    setSearchParams({ id });
  };

  const handleDrawerClose = () => {
    setSelectedTicketId(undefined);
    setSearchParams({});
  };

  // Categories from the correct pipeline's agent
  const agentTopic = PIPELINE_AGENT_TOPIC[pipelineId] ?? PIPELINE_AGENT_TOPIC["general"];
  const { data: agents } = trpc.agents.list.useQuery(
    { merchantId },
    { staleTime: 5 * 60 * 1000, enabled: !!merchantId }
  );
  const targetAgentId = useMemo(
    () => agents?.find((a) => a.topic === agentTopic)?.id,
    [agents, agentTopic]
  );
  const { data: categoriesData } =
    trpc.agents.getTransferConversationCategories.useQuery(
      { merchantId, agentId: targetAgentId || "" },
      { enabled: !!merchantId && !!targetAgentId, staleTime: 5 * 60 * 1000 }
    );
  const categories = useMemo(
    () => (categoriesData as Array<{ name: string; description: string }>) ?? [],
    [categoriesData]
  );

  const employeesList = useMemo(
    () =>
      employees?.map((e: { id: string; name: string }) => ({
        id: e.id,
        name: e.name,
      })) ?? [],
    [employees]
  );

  // Declared above the early return below: a hook after a conditional return
  // throws "Rendered more hooks than during the previous render" the moment
  // merchantId resolves from empty to set while mounted.
  const [showLegacy, setShowLegacy] = useState(false);

  if (!merchantId) {
    return (
      <div className="flex h-[calc(100dvh-60px)] items-center justify-center md:h-screen">
        <p className="text-gray-400">No hay comercio seleccionado</p>
      </div>
    );
  }

  const isDrawerOpen = !!selectedTicketId;

  return (
    <div className="flex h-full flex-col overflow-hidden bg-[#F9F9FB]">
      <TicketKanbanFilters
        filters={filters}
        onChange={setFilters}
        categories={categories}
        employees={employeesList}
      />

      <div className="flex min-h-0 flex-1 flex-col">
        <KanbanStageNavigator stages={stageNavItems} railRef={railRef} />

        {isFilterMode ? (
          <DndContext sensors={sensors} onDragEnd={handleFilterDragEnd}>
            <KanbanScrollRail ref={railRef}>
              <FilteredColumns
                merchantId={merchantId}
                pipelineId={pipelineId}
                filters={filters}
                stageGroups={stageGroups}
                onSelectTicket={handleTicketSelect}
                employeeNameById={employeeNameById}
              />
            </KanbanScrollRail>
          </DndContext>
        ) : (
          <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
            <KanbanScrollRail ref={railRef}>
              {stageGroups.map(({ stage, positions }) => {
                const meta = TICKET_STAGE_META[stage];
                return (
                  <KanbanGroup
                    key={stage}
                    stageId={stage}
                    label={meta.label}
                    hint={meta.hint}
                    accentClass={meta.accent}
                  >
                    {positions.map((pos) => (
                      <StatusColumn
                        key={pos.status}
                        merchantId={merchantId}
                        pipelineId={pipelineId}
                        status={pos.status}
                        displayName={pos.displayName}
                        onSelectTicket={handleTicketSelect}
                        employeeNameById={employeeNameById}
                        startDate={filters.startDate}
                        endDate={filters.endDate}
                      />
                    ))}
                  </KanbanGroup>
                );
              })}
            </KanbanScrollRail>
          </DndContext>
        )}
      </div>

      {/* Legacy maintenance requests (historical data) */}
      {pipelineId === "maintenance" && (
        <div className="shrink-0 border-t border-gray-200 bg-white">
          <button
            onClick={() => setShowLegacy((v) => !v)}
            className="flex w-full items-center gap-2 px-4 py-3 text-sm font-medium text-gray-600 hover:bg-gray-50"
          >
            <ChevronDown
              className={`h-4 w-4 transition-transform ${showLegacy ? "rotate-180" : ""}`}
            />
            Solicitudes anteriores (legacy)
          </button>
          {showLegacy && (
            <Suspense
              fallback={
                <div className="flex justify-center p-8">
                  <span className="text-sm text-gray-400">Cargando...</span>
                </div>
              }
            >
              <MaintenanceContent />
            </Suspense>
          )}
        </div>
      )}

      <TicketDrawer
        isOpen={isDrawerOpen}
        onClose={handleDrawerClose}
        ticketId={selectedTicketId}
        employees={employeesList}
        categories={categories}
        ticketPositions={allTicketPositions}
      />
    </div>
  );
};

export default ServiceRequestContent;
